import { useState } from 'react';
import { Button, Icon, Input, Sheet } from '@astral/ui';
import { isValidEmojiList, type EmojiPresetDTO } from '@astral/shared';
import { useT } from '../providers/I18nProvider.js';
import { useToast } from '../providers/ToastProvider.js';
import { useEmojiPresets } from '../hooks/useEmojiPresets.js';
import { EmojiSequenceEditor } from './EmojiSequenceEditor.js';

export interface EmojiPresetManagerProps {
  open: boolean;
  onClose: () => void;
  /** When provided, each preset shows a "use" action (apply / choose as default). */
  onUse?: (preset: EmojiPresetDTO) => void;
  useLabel?: string;
}

type Editing = { id: string | null; name: string; emojis: string[] } | null;

/** Create / rename / edit / reorder / delete user emoji presets (server-persisted). */
export function EmojiPresetManager({ open, onClose, onUse, useLabel }: EmojiPresetManagerProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { presets, loading, create, update, remove } = useEmojiPresets(open);
  const [editing, setEditing] = useState<Editing>(null);
  const [busy, setBusy] = useState(false);

  const startNew = (): void => setEditing({ id: null, name: '', emojis: [] });
  const startEdit = (p: EmojiPresetDTO): void => setEditing({ id: p.id, name: p.name, emojis: p.emojis });

  const canSave = editing !== null && editing.name.trim().length > 0 && isValidEmojiList(editing.emojis.slice(0, 20)) && editing.emojis.length > 0;

  const save = async (): Promise<void> => {
    if (!editing || editing.name.trim().length === 0 || editing.emojis.length === 0) return;
    setBusy(true);
    const payload = { name: editing.name.trim(), emojis: editing.emojis };
    const result = editing.id ? await update(editing.id, payload) : await create(payload);
    setBusy(false);
    if (result) {
      toast.show(editing.id ? t('presets.updated') : t('presets.created'), 'success');
      setEditing(null);
    } else {
      toast.show(t('presets.saveFailed'), 'error');
    }
  };

  const del = async (id: string): Promise<void> => {
    if (await remove(id)) {
      toast.show(t('presets.deleted'), 'default');
      setEditing(null);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={() => {
        setEditing(null);
        onClose();
      }}
      title={t('presets.title')}
    >
      {editing ? (
        <div className="stack" style={{ gap: 14 }}>
          <Input
            label={t('presets.name')}
            placeholder={t('presets.namePlaceholder')}
            value={editing.name}
            maxLength={40}
            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
          />
          <div className="stack" style={{ gap: 8 }}>
            <span className="as-field__label">{t('presets.sequenceLabel')}</span>
            <EmojiSequenceEditor emojis={editing.emojis} onChange={(emojis) => setEditing({ ...editing, emojis })} />
          </div>
          <div className="stack" style={{ gap: 8 }}>
            <Button fullWidth size="lg" loading={busy} disabled={!canSave} onClick={() => void save()}>
              {t('presets.save')}
            </Button>
            <div className="row-between">
              <Button variant="ghost" onClick={() => setEditing(null)}>
                {t('common.cancel')}
              </Button>
              {editing.id && (
                <Button variant="ghost" leftIcon={<Icon name="trash" size={16} />} onClick={() => void del(editing.id as string)}>
                  {t('common.delete')}
                </Button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="stack" style={{ gap: 12 }}>
          <Button fullWidth variant="secondary" leftIcon={<Icon name="plus" size={18} />} onClick={startNew}>
            {t('presets.create')}
          </Button>

          {loading && presets.length === 0 ? (
            <p className="muted">{t('common.loading')}</p>
          ) : presets.length === 0 ? (
            <p className="muted">{t('presets.empty')}</p>
          ) : (
            <div className="stack" style={{ gap: 8 }}>
              {presets.map((p) => (
                <div key={p.id} className="preset-row">
                  <div className="preset-row__main">
                    <span className="preset-row__name">{p.name}</span>
                    <span className="preset-row__emojis">{p.emojis.join(' ')}</span>
                  </div>
                  <div className="preset-row__actions">
                    {onUse && (
                      <Button
                        size="sm"
                        onClick={() => {
                          onUse(p);
                        }}
                      >
                        {useLabel ?? t('presets.apply')}
                      </Button>
                    )}
                    <button className="icon-btn icon-btn--sm" aria-label={t('presets.edit')} onClick={() => startEdit(p)}>
                      <Icon name="edit" size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Sheet>
  );
}
